import { c as _c } from "react/compiler-runtime";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { ArrowUp, Plus } from "lucide-react";
import { AppHeaderBar, PanelTogglePlaceholder } from "@/components/ui/app-header";
import { Skeleton } from "@/components/ui/skeleton";
import { peekSkeletonComposerText, readCachedShowAutofixCard, saveSkeletonComposerText } from "./newSessionSkeletonState";
import { NEW_SESSION_MASCOT_SIZE_CLASS, NEW_SESSION_SCROLL_AREA_CLASS, NEW_SESSION_WELCOME_COLUMN_CLASS } from "./newSessionLayout";
import { PROMPT_PILL_ROW_CLASS, PromptPillSkeleton, LEAD_STARTER_PILL_LAYOUT_CLASS, STARTER_PILL_ROW_CLASS, StarterPromptPillSkeleton, SuggestionCardSkeleton } from "./promptPills";
import { SidebarSkeleton } from "./SidebarSkeleton";
import { orgSlugFromPath, readLeftSidebarSkeletonLayout, useIsHydrated } from "./skeletonLayout";
import { assetUrl } from "@/lib/assetUrl";

/* ---------------------------------------------------------------------------
 * Structural skeletons for the Codesmith session view.
 *
 * SessionLoadingScreen    – centered animated anvil for existing sessions.
 *                           Used inside Codesmith.tsx where the real sidebar
 *                           is already rendered alongside.
 *
 * NewSessionChatSkeleton  – the new-session welcome layout (centered mascot,
 *                           greeting, composer, starter rows).
 *
 * SessionSkeleton         – sidebar + main pane. Used in the page-level
 *                           Suspense fallback where the entire Codesmith view
 *                           is replaced; picks the main-pane variant from the
 *                           current route.
 * -------------------------------------------------------------------------*/

/** Loading state for an existing session. */
export function SessionLoadingScreen() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div role="status" aria-label="Loading session" className="flex min-w-0 flex-1 items-center justify-center bg-[var(--color-app-background)]"><span aria-hidden={true} className="flex size-20 items-center justify-center"><img src={assetUrl("anvil-animations/anvil-ruminating-light.png")} alt="" width={80} height={80} draggable={false} className="select-none object-contain [image-rendering:pixelated] dark:hidden" /><img src={assetUrl("anvil-animations/anvil-ruminating-dark.png")} alt="" width={80} height={80} draggable={false} className="hidden select-none object-contain [image-rendering:pixelated] dark:block" /></span></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/**
 * `NewSessionView` mounts PrActionCards only when `pr_preview` hydrates a
 * `previewManagedPr` (see Codesmith.tsx) — the "View in Codesmith" deep link
 * carries the PR metadata as JSON in that param. A bare `pr` param (a
 * resolve-pr landing with no metadata, or a `repo`+`pr` Fix-with-Codesmith
 * link) resolves into the autofix card + TaskStarterCards instead, so only
 * `pr_preview` reserves the PrActionCards skeleton shape (four action pills,
 * no suggestion card).
 */
function hasPrPreviewInSearch(search: string): boolean {
  return new URLSearchParams(search).has("pr_preview");
}

/** Placeholder pill count matching PrActionCards' four action pills. */
const PR_ACTION_PILL_COUNT = 4;

/**
 * Mirrors NewSessionView's non-compact layout so the fallback resolves into
 * the real page without reshaping. The mascot and composer are static chrome
 * rendered for real; the user-dependent greeting and starter rows shimmer.
 * The composer is a live textarea whose text hands off
 * to the real composer's draft (see newSessionSkeletonState), so the user
 * can start typing before the app finishes loading.
 */
export function NewSessionChatSkeleton() {
  const $ = _c(33);
  const isHydrated = useIsHydrated();
  let isPrAttached;
  let t0;
  if ($[0] !== isHydrated) {
    const orgSlug = isHydrated ? orgSlugFromPath(window.location.pathname) : null;
    isPrAttached = isHydrated && hasPrPreviewInSearch(window.location.search);
    t0 = isHydrated && !isPrAttached && readCachedShowAutofixCard(orgSlug);
    $[0] = isHydrated;
    $[1] = isPrAttached;
    $[2] = t0;
  } else {
    isPrAttached = $[1];
    t0 = $[2];
  }
  const showAutofixCardSkeleton = t0;
  let t1;
  if ($[3] !== isHydrated) {
    t1 = isHydrated && !readLeftSidebarSkeletonLayout().visible && <PanelTogglePlaceholder />;
    $[3] = isHydrated;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <AppHeaderBar inset="canvas" className="bg-app-background">{t1}</AppHeaderBar>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="relative mt-1"><img src={assetUrl("anvil-hello-light.png")} alt="[code]smith" className={`${NEW_SESSION_MASCOT_SIZE_CLASS} dark:hidden`} /><img src={assetUrl("anvil-hello-dark.png")} alt="[code]smith" className={`hidden ${NEW_SESSION_MASCOT_SIZE_CLASS} dark:block`} /></div>;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex flex-col items-center">{t3}<div className="flex h-[25px] items-center"><Skeleton className="h-5 w-72 max-w-full rounded" /></div></div>;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== isHydrated) {
    t5 = isHydrated ? peekSkeletonComposerText() : "";
    $[9] = isHydrated;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t5) {
    t6 = <textarea rows={1} autoFocus={true} autoComplete="off" data-1p-ignore={true} spellCheck={false} aria-label="Chat input" placeholder="Got a task or question?" defaultValue={t5} onChange={_temp} className="type-chat-prose pointer-events-auto w-full resize-none overflow-hidden border-0 bg-transparent p-0 pl-[31px] md:pl-[29px] pr-9 text-neutral-900 placeholder-neutral-500 outline-none ring-0 focus:border-0 focus:outline-none focus:ring-0 dark:text-neutral-100 dark:placeholder-neutral-500/70" />;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="absolute inset-y-0 left-0 flex items-center gap-1"><span className="-ml-2 md:-ml-[10px] flex size-8 items-center justify-center rounded-full text-icon-secondary"><Plus className="size-[18px]" strokeWidth={2} aria-hidden={true} /></span></div>;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="absolute inset-y-0 right-0 flex items-center"><span className="flex h-7 w-7 items-center justify-center rounded-full border border-black/[0.08] bg-gradient-to-r from-[var(--brand-gradient-blue-tertiary)] to-[var(--brand-gradient-yellow-tertiary)] text-neutral-400 dark:text-neutral-500"><ArrowUp className="size-3.5" strokeWidth={2.5} aria-hidden={true} /></span></div>;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t6) {
    t9 = <div className="rounded-[32px] border border-[var(--color-border)] bg-white pl-3 pr-1.5 py-1 shadow-[0px_10px_20px_0px_rgba(0,0,0,0.08)] md:pl-4 md:pr-2 md:py-1.5 dark:bg-neutral-900 dark:shadow-[0px_10px_20px_0px_rgba(0,0,0,0.3)]"><div className="relative flex min-h-8 items-center">{t6}{t7}{t8}</div></div>;
    $[15] = t6;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== showAutofixCardSkeleton) {
    t10 = showAutofixCardSkeleton && <SuggestionCardSkeleton />;
    $[17] = showAutofixCardSkeleton;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const t11 = isPrAttached ? PROMPT_PILL_ROW_CLASS : STARTER_PILL_ROW_CLASS;
  let t12;
  if ($[19] !== isPrAttached) {
    t12 = isPrAttached ? Array.from({
      length: PR_ACTION_PILL_COUNT
    }, _temp2) : <><StarterPromptPillSkeleton className={LEAD_STARTER_PILL_LAYOUT_CLASS} /><StarterPromptPillSkeleton className="grow" /></>;
    $[19] = isPrAttached;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t11 || $[22] !== t12) {
    t13 = <div className={t11}>{t12}</div>;
    $[21] = t11;
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t10 || $[25] !== t13) {
    t14 = <div className="flex w-full flex-col gap-4">{t10}{t13}</div>;
    $[24] = t10;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t14 || $[28] !== t9) {
    t15 = <div className={NEW_SESSION_SCROLL_AREA_CLASS}><div className={`relative mb-auto flex max-w-full flex-col px-4 ${NEW_SESSION_WELCOME_COLUMN_CLASS}`}>{t4}<div className="flex w-full flex-col gap-3">{t9}{t14}</div></div></div>;
    $[27] = t14;
    $[28] = t9;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t15 || $[31] !== t2) {
    t16 = <div className="pointer-events-none flex min-w-0 flex-1 flex-col bg-[var(--color-app-background)]">{t2}{t15}</div>;
    $[30] = t15;
    $[31] = t2;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  return t16;
}

/**
 * `/[org]/sessions` and `/[org]/sessions/new` land on NewSessionView, so the
 * fallback should be welcome-shaped there rather than chat-transcript-shaped.
 */
function _temp2(_, i) {
  return <PromptPillSkeleton key={i} className="w-32" />;
}
function _temp(e) {
  return saveSkeletonComposerText(e.target.value);
}
function isNewSessionPath(path: string): boolean {
  const segments = path.split(/[?#]/)[0].split("/").filter(Boolean);
  return segments[1] === "sessions" && (segments.length === 2 || segments[2] === "new");
}

/** Full skeleton (sidebar + main pane) for the page-level Suspense fallback. */
export function SessionSkeleton() {
  const $ = _c(3);
  const router = useRouter();
  const isHydrated = useIsHydrated();
  const path = isHydrated ? window.location.pathname : router.asPath;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <SidebarSkeleton />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== path) {
    t1 = <div className="pointer-events-none flex h-full">{t0}{isNewSessionPath(path) ? <NewSessionChatSkeleton /> : <SessionLoadingScreen />}</div>;
    $[1] = path;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}